import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spin } from "antd";
import apiFactory from "../../api";
import { socketStatus$ } from "../../api/webSocketRx";

const API_BASE_URL =
  process.env.REACT_APP_AUTHENTICATION_API_URL ||
  "https://winitech.com.vn:8300";

export const SmartVideoPlayer = ({ file, cssClass }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countReconnectedSuccessWs, setCountReconnectedSuccessWs] = useState(1);

  const videoRef = useRef(null);
  const seekTimeRef = useRef(0);

  const desiredSeekTimeRef = useRef(null);

  const abortControllerRef = useRef(null);

  const getTicketedUrl = useCallback(async (resourceId, signal) => {
    if (!resourceId) return;

    try {
      const response = await apiFactory.resourceApi.getVideoTicket(
        resourceId,
        signal
      );

      if (response?.status === 200 && response?.data?.url) {
        return new URL(response.data.url, API_BASE_URL).href;
      }

      throw new Error(
        response?.data?.message || "Invalid response from ticket API"
      );
    } catch (err) {
      console.error("Failed to get ticket:", err);
      throw err;
    }
  }, []);

  useEffect(() => {
    const sub = socketStatus$.subscribe((eventData) => {
      if (!eventData) return;

      if (
        eventData?.status === "CONNECTED" &&
        countReconnectedSuccessWs < eventData?.connectedSuccess
      ) {
        setCountReconnectedSuccessWs(eventData?.connectedSuccess);
      }
    });

    return () => sub.unsubscribe();
  }, [file?.resourceId]);

  useEffect(() => {
    if (!file || !file?.resourceId) {
      setLoading(false);
      return;
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const loadInitialVideo = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = await getTicketedUrl(file.resourceId, signal);

        if (!signal.aborted) {
          setVideoUrl(url);
        }
      } catch (err) {
        if (!signal.aborted) {
          setError("Video could not be loaded. Please try again.");
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadInitialVideo();

    return () => abortControllerRef.current?.abort();
  }, [file?.resourceId, getTicketedUrl]);

  useEffect(() => {
    if (countReconnectedSuccessWs > 0) {
      if (!file || !file?.resourceId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      const loadInitialVideo = async () => {
        setLoading(true);
        setError(null);
        try {
          const url = await getTicketedUrl(file.resourceId, signal);

          if (!signal.aborted) {
            setVideoUrl(url);
          }
        } catch (err) {
          if (!signal.aborted) {
            setError("Video could not be loaded. Please try again.");
          }
        } finally {
          if (!signal.aborted) {
            setLoading(false);
          }
        }
      };

      loadInitialVideo();

      return () => abortControllerRef.current?.abort();
    }
  }, [countReconnectedSuccessWs, file?.resourceId, getTicketedUrl]);

  const handleError = async () => {
    if (!file?.resourceId) return;

    seekTimeRef.current =
      desiredSeekTimeRef.current !== null
        ? desiredSeekTimeRef.current
        : videoRef.current
          ? videoRef.current.currentTime
          : 0;

    desiredSeekTimeRef.current = null;

    setLoading(true);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const newUrl = await getTicketedUrl(file.resourceId, signal);
      if (!signal.aborted) {
        setVideoUrl(newUrl);
      }
    } catch (err) {
      if (!signal.aborted) {
        setError("Could not refresh the viewing session. Please try again.");
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  };

  const handleCanPlay = () => {
    const videoElement = videoRef.current;

    desiredSeekTimeRef.current = null;

    if (videoElement && seekTimeRef.current > 0) {
      videoElement.currentTime = seekTimeRef.current;
      seekTimeRef.current = 0;
    }
  };

  const handleSeeking = () => {
    if (videoRef.current) {
      desiredSeekTimeRef.current = videoRef.current.currentTime;
    }
  };

  if (loading) return <Spin tip="Video loading..." />;

  if (error && !videoUrl)
    return <p style={{ color: "white", textAlign: "center" }}>{error}</p>;

  return (
    <>
      {videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          autoPlay
          width="100%"
          controlsList="nodownload"
          onError={handleError}
          onCanPlay={handleCanPlay}
          onSeeking={handleSeeking}
          className={cssClass}
        >
          The browser does not support the video tag.
        </video>
      )}
    </>
  );
};
